import { UserChatEntry } from 'dragonmouse-shared/interfaces';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectActiveSceneChat, selectFrames, selectUserSettings } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

const STORAGE_KEY = 'debug-overlay-position';

const loadPosition = (): { x: number; y: number } | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
	} catch {}
	return null;
};

const ChatDebugOverlay = () => {
	const overlayRef = useRef<HTMLDivElement>(null);
	const dragging = useRef(false);
	const dragOffset = useRef({ x: 0, y: 0 });
	const [pos, setPos] = useState<{ x: number; y: number } | null>(loadPosition);

	const { activeChat, frames, debugModeEnabled } = useSelector(
		(r: RootReducer) => ({
			activeChat: selectActiveSceneChat(r.db, r.mode.activeSceneChatId),
			frames: selectFrames(r.db, true),
			debugModeEnabled: selectUserSettings(r.db).debugModeEnabled,
		}),
		shallowEqual
	);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	const stats = useMemo(() => {
		if (!activeChat) return null;
		const contextTokens = activeChat.metadata?.contextTokens;
		const contextPercent = contextTokens ? Math.round((contextTokens / 200_000) * 100) : null;
		const userPrompts = activeChat.entries.filter((e) => e.kind === 'user').length;
		const attachments = activeChat.entries
			.filter((e): e is UserChatEntry => e.kind === 'user')
			.reduce((sum, e) => sum + (e.attachments?.length || 0), 0);
		return {
			version: activeChat.metadata?.version || '—',
			contextPercent,
			userPrompts,
			frameCount: frames.length,
			attachments,
		};
	}, [activeChat, frames.length]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!overlayRef.current) return;
		dragging.current = true;
		const rect = overlayRef.current.getBoundingClientRect();
		dragOffset.current = {
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		};

		const onMouseMove = (ev: MouseEvent) => {
			if (!dragging.current) return;
			const x = Math.max(0, Math.min(ev.clientX - dragOffset.current.x, window.innerWidth - 120));
			const y = Math.max(0, Math.min(ev.clientY - dragOffset.current.y, window.innerHeight - 80));
			setPos({ x, y });
		};

		const onMouseUp = () => {
			dragging.current = false;
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
			if (overlayRef.current) {
				const rect = overlayRef.current.getBoundingClientRect();
				const p = { x: rect.left, y: rect.top };
				localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
			}
		};

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
	}, []);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (!debugModeEnabled || !activeChat || !stats) return null;

	return (
		<S.Base ref={overlayRef} $x={pos?.x} $y={pos?.y} onMouseDown={onMouseDown}>
			<S.Title>Debug</S.Title>
			<S.Row>
				<S.Label>ver</S.Label>
				<S.Value>{stats.version}</S.Value>
			</S.Row>
			{stats.contextPercent !== null && (
				<S.Row>
					<S.Label>ctx</S.Label>
					<S.Value>~{stats.contextPercent}%</S.Value>
				</S.Row>
			)}
			<S.Row>
				<S.Label>prompts</S.Label>
				<S.Value>{stats.userPrompts}</S.Value>
			</S.Row>
			<S.Row>
				<S.Label>frames</S.Label>
				<S.Value>{stats.frameCount}</S.Value>
			</S.Row>
			{stats.attachments > 0 && (
				<S.Row>
					<S.Label>assets</S.Label>
					<S.Value>{stats.attachments}</S.Value>
				</S.Row>
			)}
		</S.Base>
	);
};

export default ChatDebugOverlay;
